import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Confirm, Dropdown, Modal } from "semantic-ui-react";
import {
  copyForm,
  duplicateDocAndAnnots,
} from "../../../app/firestore/firebaseService";
import {
  deleteDocAnnotsInDb,
  deleteDocInDb,
  deleteTasksFromDocInDb,
  fetchDocAnnotsAllFromDb,
  updateDocInDb,
} from "../../../app/firestore/firestoreService";
import { useMediaQuery } from "react-responsive";
import { openModal } from "../../../app/common/modals/modalSlice";
import {
  docIsTypeImage,
  docIsTypeLink,
  docIsTypePdf,
  docSigningVerificationEnabled,
  downloadNonPdf,
  downloadPdf,
  downloadSigningCertificatePdf,
  printPdf,
} from "../../../app/common/util/util";
import { getFormFieldData } from "./docFill/formFieldData/formFieldData";

export default function DocActionButtons({ doc }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copyDuplicateOpen, setCopyDuplicateOpen] = useState(false);
  const { transaction } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { forms } = useSelector((state) => state.doc);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  let { id } = useParams();

  const docIsLink = docIsTypeLink(doc);
  const docIsPdf = !docIsLink && docIsTypePdf(doc);
  const docIsImage = docIsTypeImage(doc);

  const deleteMessage = `Are you sure you want to delete ${doc.title} ?`;

  const agentRepresents = transaction?.agentRepresents;
  const signatureFieldName =
    agentRepresents === "Buyer"
      ? "Signature Buyer Broker"
      : "Signature Seller Broker";
  // const isUserTransactionAgentOrCoagent = transaction?.userId === currentUserProfile?.id;

  const showAgentSignOption = useMemo(() => {
    // if (!isUserTransactionAgent) return false;
    if (doc.status !== "In Progress" && doc.status !== "Awaiting Signature" && doc.status !== "Complete" ) return false;
    if (!doc.isStateForm || !docIsPdf) return false;
    if (!agentRepresents) return false;
    // Verify the form actually has this broker signature field
    const formFields = getFormFieldData(doc.title, transaction);
    const hasSignatureField = formFields?.some(
      (f) => f.name === signatureFieldName
    );
    if (!hasSignatureField) return false;
    // Only show if the field is still blank (agent hasn't signed yet)
    const fieldValue = doc.formFieldValues?.[signatureFieldName];
    if (fieldValue) return false;
    return true;
  }, [doc, transaction, agentRepresents, signatureFieldName, docIsPdf]);

  function handleAgentSign() {
    navigate(
      `/transactions/${doc.transactionId}/documents/${doc.id}/view`,
      { state: { openAgentSignModal: true } }
    );
  }

  function handleGoToTransaction() {
    navigate(`/transactions/${doc.transactionId}/overview`);
  }

  function handleGoToLink() {
    if (doc.linkUrl) {
      window.open(doc.linkUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.error("No link found for this document.");
    }
  }

  function handleView() {
    // if (doc.shared && doc.subStatus === "Newly Shared") {
    //   updateDocInDb(doc.id, { subStatus: "Received" });
    // }
    if (docIsPdf) {
      navigate(`/transactions/${doc.transactionId}/documents/${doc.id}/view`);
    } else if (docIsImage) {
      dispatch(
        openModal({
          modalType: "DocViewImageFileModal",
          modalProps: { doc: doc },
        })
      );
    } else {
      toast.error("Error with viewing document.");
    }
  }

  function handleFillOutForm() {
    updateDocInDb(doc.id, { subStatus: "Filling Out Form" });
    navigate(`/transactions/${doc.transactionId}/documents/${doc.id}/fill`);
  }

  function handlePrepareSignatures() {
    updateDocInDb(doc.id, { subStatus: "Preparing Signatures" });
    navigate(`/transactions/${doc.transactionId}/documents/${doc.id}/prepare`);
  }

  async function handleDownload() {
    // if (doc.shared && doc.subStatus === "Newly Shared") {
    //   updateDocInDb(doc.id, { subStatus: "Received" });
    // }
    if (docIsPdf) {
      downloadPdf(doc, transaction, currentUserProfile);
    } else if (doc.filetypeMIME) {
      downloadNonPdf(doc);
    } else {
      toast.error("Error with downloading document.");
    }
  }

  async function handleDownloadSigningCertificate() {
    downloadSigningCertificatePdf(doc, currentUserProfile);
  }

  async function handlePrint() {
    if (docIsPdf) {
      printPdf(doc, transaction, currentUserProfile);
    } else if (docIsImage) {
      const printWindow = window.open(doc.docUrl, "_blank");
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } else {
      toast.error("Error printing document.");
    }
  }

  async function handleCopy() {
    if (doc.isStateForm) {
      const originalForm = forms.filter((form) => form.id === doc.formId)[0];
      if (originalForm) {
        copyForm(transaction, doc, originalForm);
      }
    }
  }

  async function handleDuplicate() {
    let annots = [];
    if (!doc.pdfBurnVersion) {
      annots = await fetchDocAnnotsAllFromDb(doc.id);
    }
    duplicateDocAndAnnots(transaction, doc, annots);
  }

  async function handleSigningStatus() {
    try {
      const newStatus = doc.signingComplete === true ? false : true;
      await updateDocInDb(
        doc.id,
        {
          signingComplete: newStatus,
        },
        true
      );
      toast.success("Signing status updated");
    } catch (error) {
      toast.error(error.message);
    } finally {
    }
  }

  function handleArchive() {
    dispatch(
      openModal({
        modalType: "DocArchiveLabelModal",
        modalProps: { doc: doc },
      })
    );
  }

  async function handleUnarchive() {
    try {
      await updateDocInDb(doc.id, {
        archived: false,
      });
      toast.success("Document successfully unarchived");
    } catch (error) {
      toast.error(error.message);
    } finally {
    }
  }

  function handleExtractTasksFromForm() {
    dispatch(
      openModal({
        modalType: "DocExtractDeadlines",
        modalProps: { doc: doc },
      })
    );
  }

  function handleRename() {
    dispatch(
      openModal({
        modalType: "DocRenameModal",
        modalProps: { doc: doc },
      })
    );
  }

  function handleEditArchiveLabel() {
    dispatch(
      openModal({
        modalType: "DocEditArchiveLabelModal",
        modalProps: { doc: doc },
      })
    );
  }

  async function handleDelete() {
    try {
      if (!doc.pdfBurnVersion) {
        await deleteDocAnnotsInDb(doc.id);
      }
      await deleteDocInDb(doc.id);
      if (!doc.pdfBurnVersion || (!doc.isStateForm && !doc.formId)) {
        console.log("THIS WOULD DELETE THE PDF!!!!!!");
        // deleteDocFromStorage(doc.docRef);
      }
      await deleteTasksFromDocInDb(doc.id);
      toast.success("Document successfully deleted");
    } catch (error) {
      toast.error(error.message);
    }
  }

  return (
    <div className={isMobile ? null : "text align right"}>
      <Dropdown
        button
        icon="chevron down"
        text={isMobile ? "Actions " : null}
        className={isMobile ? "button icon" : "button mini icon"}
        direction={isMobile ? "right" : "left"}
      >
        <Dropdown.Menu>
          {!id && (
            <Dropdown.Item onClick={() => handleGoToTransaction()}>
              Go To Transaction
            </Dropdown.Item>
          )}
          {docIsLink && (
            <Dropdown.Item onClick={() => handleGoToLink()}>
              Go to Link
            </Dropdown.Item>
          )}
          {(docIsPdf || docIsImage) && (
            <Dropdown.Item onClick={() => handleView()}>View/Comment</Dropdown.Item>
          )}
          {showAgentSignOption && (
            <Dropdown.Item onClick={() => handleAgentSign()}>
              Sign
            </Dropdown.Item>
          )}
          {docIsPdf &&
            doc.status === "In Progress" &&
            doc.isStateForm &&
            !doc.shared &&
            !doc.signingComplete && (
              <Dropdown.Item onClick={() => handleFillOutForm()}>
                Fill Out
              </Dropdown.Item>
            )}
          {docIsPdf && !doc.signingSentOutAt && (
            <Dropdown.Item onClick={() => handlePrepareSignatures()}>
              Prepare Signatures
            </Dropdown.Item>
          )}
          {!docIsLink &&
            (doc.signingComplete === true ? (
              <Dropdown.Item onClick={() => handleSigningStatus()}>
                Undo Signing Complete
              </Dropdown.Item>
            ) : (
              <Dropdown.Item onClick={() => handleSigningStatus()}>
                Mark Signing Complete
              </Dropdown.Item>
            ))}
          {!docIsLink && (
            <Dropdown.Item onClick={() => handleDownload()}>
              Download
            </Dropdown.Item>
          )}
          {(docIsPdf || docIsImage) && (
            <Dropdown.Item onClick={() => handlePrint()}>Print</Dropdown.Item>
          )}
          {!docIsLink &&
            docSigningVerificationEnabled(doc, currentUserProfile) && (
              <Dropdown.Item onClick={() => handleDownloadSigningCertificate()}>
                Signing Certificate
              </Dropdown.Item>
            )}
          {docIsPdf &&
            (!doc.isStateForm || (doc.isStateForm && doc.hasTasks)) && (
              <Dropdown.Item onClick={() => handleExtractTasksFromForm()}>
                Extract Deadlines
              </Dropdown.Item>
            )}
          {docIsPdf && doc.isStateForm && (
            <Dropdown.Item onClick={() => setCopyDuplicateOpen(true)}>
              Copy/Duplicate
            </Dropdown.Item>
          )}
          {docIsPdf && !doc.isStateForm && (
            <Dropdown.Item onClick={() => handleDuplicate()}>
              Duplicate
            </Dropdown.Item>
          )}

          {doc.archived === true ? (
            <>
              <Dropdown.Item onClick={() => handleUnarchive()}>
                Unarchive
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleEditArchiveLabel()}>
                Edit Archive Label
              </Dropdown.Item>
            </>
          ) : (
            <Dropdown.Item onClick={() => handleArchive()}>
              Archive/Folder
            </Dropdown.Item>
          )}
          {!docIsLink && (
            <Dropdown.Item onClick={() => handleRename()}>Rename</Dropdown.Item>
          )}
          <Dropdown.Item onClick={() => setConfirmOpen(true)}>
            Delete
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <Confirm
        open={confirmOpen}
        content={deleteMessage}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete()}
      ></Confirm>
      <Modal
        size="tiny"
        open={copyDuplicateOpen}
        onClose={() => setCopyDuplicateOpen(false)}
      >
        <Modal.Header>Copy or Duplicate Form</Modal.Header>
        <Modal.Content>
          <p>
            <strong>Copy Form</strong> creates a new copy of this form with
            signatures removed, returned to edit mode.
          </p>
          <p>
            <strong>Duplicate</strong> creates a new, non-editable copy of
            this form with signatures kept intact.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCopyDuplicateOpen(false)}>Cancel</Button>
          <Button
          primary
            onClick={() => {
              setCopyDuplicateOpen(false);
              handleDuplicate();
            }}
          >
            Duplicate
          </Button>
          <Button
            primary
            onClick={() => {
              setCopyDuplicateOpen(false);
              handleCopy();
            }}
          >
            Copy Form
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
